import { useCallback, useEffect, useRef, useState } from 'react';
import { requestResizeArtboard } from '@/lib/artboardSize';
import { applyClientBeautyToUrl } from '@/lib/applyClientBeauty';
import { BEAUTY_PRESETS, type BeautyPresetId } from '@/lib/beautyPresets';
import { createKenBurnsReel, downloadBlob } from '@/lib/createKenBurnsReel';
// FUTURE PHASE: cloud AI callables + credits — re-enable with PHOTO_STUDIO_AI_ENABLED
import {
  // callAdjustSkinTone,
  // callCreateReel,
  // callEnhancePortrait,
  // callReplaceBackground,
  // callSwapOutfit,
  ensureHttpImageUrl,
  // fetchAiCredits,
  // type AiCredits,
  // type AiCreditsSnapshot,
} from '@/lib/photoStudioAi';
import {
  listProjectReels,
  persistLocalReel,
  // resolveReelPlaybackUrl,
  type StudioReel,
} from '@/lib/reelStore';
import {
  // estimatedAiStage,
  IDLE_REEL_PROGRESS,
  REEL_STAGE_CHIPS,
  reelProgressAt,
  type ReelProgress,
} from '@/lib/reelProgress';
// import { SOCIAL_BG_PRESETS } from '@/data/socialBackgrounds';
import {
  getMotionStyle,
  getReelPlatform,
  MOTION_STYLES,
  REEL_PLATFORMS,
  type MotionStyleId,
} from '@/data/reelPlatforms';
// import { SUIT_CATALOG } from '@/data/suitCatalog';
import { getClientAuth } from '@canvas/firebase-config/client';

/**
 * FUTURE PHASE: set to `true` when shipping Photo Studio cloud AI + credits
 * (enhance, skin, suit, background, AI motion). Free local beauty/motion stay on.
 */
const PHOTO_STUDIO_AI_ENABLED = false;

type StudioStep = 'destination' | 'photo' | 'look' | 'motion' | 'export';
type LookTab = 'beauty' | 'skin' | 'suit' | 'background';

const STEPS: { id: StudioStep; label: string }[] = [
  { id: 'destination', label: 'Platform' },
  { id: 'photo', label: 'Photo' },
  { id: 'look', label: 'Look' },
  { id: 'motion', label: 'Motion' },
  { id: 'export', label: 'Export' },
];

interface PhotoStudioPanelProps {
  projectId: string;
  onPlaceImage: (url: string) => void | Promise<boolean | void>;
  onUploadFile?: (file: File) => void | Promise<void>;
}

export default function PhotoStudioPanel({
  projectId,
  onPlaceImage,
  onUploadFile,
}: PhotoStudioPanelProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const reelProgressTimer = useRef<number | null>(null);
  const dismissedReelJob = useRef(false);

  const [step, setStep] = useState<StudioStep>('destination');
  const [lookTab, setLookTab] = useState<LookTab>('beauty');
  const [platformId, setPlatformId] = useState('instagram');
  const [motionStyleId, setMotionStyleId] = useState<MotionStyleId>('push-in');
  const [showSafeZone, setShowSafeZone] = useState(true);
  const [workingUrl, setWorkingUrl] = useState<string | null>(null);
  /** Local blob/data URL kept for CORS-safe motion encode */
  const [localImageUrl, setLocalImageUrl] = useState<string | null>(null);
  const [beforeUrl, setBeforeUrl] = useState<string | null>(null);
  const [showBefore, setShowBefore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  // FUTURE PHASE: AI look tool state
  // const [skinAmount, setSkinAmount] = useState(0);
  // const [bgId, setBgId] = useState('ig-purple');
  // const [suitId, setSuitId] = useState('navy-suit');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  // FUTURE PHASE: AI credits UI state
  // const [credits, setCredits] = useState<AiCreditsSnapshot | null>(null);
  // const [creditsError, setCreditsError] = useState<string | null>(null);
  // const [creditsLoading, setCreditsLoading] = useState(true);
  const [signedIn, setSignedIn] = useState(() => Boolean(getClientAuth().currentUser));
  const [reelUrl, setReelUrl] = useState<string | null>(null);
  const [savedReels, setSavedReels] = useState<StudioReel[]>([]);
  const [reelsLoading, setReelsLoading] = useState(false);
  const [reelProgress, setReelProgress] = useState<ReelProgress>(IDLE_REEL_PROGRESS);

  const platform = getReelPlatform(platformId);
  const availableMotionStyles = PHOTO_STUDIO_AI_ENABLED
    ? MOTION_STYLES
    : MOTION_STYLES.filter((m) => m.costCredits === 0);
  const reelActive =
    reelProgress.stage !== 'idle' &&
    reelProgress.stage !== 'ready' &&
    reelProgress.stage !== 'failed';

  /* FUTURE PHASE: AI credits
  const refreshCredits = useCallback(async () => {
    const user = getClientAuth().currentUser;
    if (!user) {
      setCredits(null);
      setCreditsError(null);
      setCreditsLoading(false);
      return;
    }
    setCreditsLoading(true);
    setCreditsError(null);
    try {
      const snap = await fetchAiCredits();
      setCredits(snap);
      if (!snap) setCreditsError('Could not load credits');
    } catch (err) {
      setCredits(null);
      setCreditsError(err instanceof Error ? err.message : 'Could not load credits');
    } finally {
      setCreditsLoading(false);
    }
  }, []);
  */

  const refreshSavedReels = useCallback(async () => {
    if (!projectId || !getClientAuth().currentUser) {
      setSavedReels([]);
      return;
    }
    setReelsLoading(true);
    try {
      setSavedReels(await listProjectReels(projectId));
    } catch (err) {
      console.warn('Failed to load saved reels', err);
    } finally {
      setReelsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    const auth = getClientAuth();
    const unsub = auth.onAuthStateChanged((user) => {
      setSignedIn(Boolean(user));
      if (user) {
        // if (PHOTO_STUDIO_AI_ENABLED) void refreshCredits();
        void refreshSavedReels();
      } else {
        // setCredits(null);
        // setCreditsError(null);
        // setCreditsLoading(false);
        setSavedReels([]);
      }
    });
    return () => unsub();
  }, [/* refreshCredits, */ refreshSavedReels]);

  useEffect(() => {
    if (signedIn && projectId) void refreshSavedReels();
  }, [projectId, signedIn, refreshSavedReels]);
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (reelUrl) URL.revokeObjectURL(reelUrl);
      if (reelProgressTimer.current) window.clearInterval(reelProgressTimer.current);
    };
  }, [reelUrl]);

  useEffect(() => {
    if (!cameraOpen) return;
    let cancelled = false;
    (async () => {
      try {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play();
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [facingMode, cameraOpen]);

  /* FUTURE PHASE: AI credits
  const applyCredits = (next?: AiCredits) => {
    if (!next || !credits) return;
    setCredits({ ...credits, credits: next });
  };
  */

  const clearReelProgressTimer = () => {
    if (reelProgressTimer.current) {
      window.clearInterval(reelProgressTimer.current);
      reelProgressTimer.current = null;
    }
  };

  const selectPlatform = (id: string) => {
    const p = getReelPlatform(id);
    setPlatformId(id);
    // FUTURE PHASE: setBgId(p.defaultBgId);
    requestResizeArtboard(p.width, p.height);
  };

  const setPhoto = (url: string) => {
    setWorkingUrl(url);
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      setLocalImageUrl(url);
    }
    setBeforeUrl(url);
    setShowBefore(false);
    if (reelUrl) URL.revokeObjectURL(reelUrl);
    setReelUrl(null);
    setReelProgress(IDLE_REEL_PROGRESS);
    setStep('look');
    setNote(null);
  };

  const encodeSourceUrl = () => localImageUrl || workingUrl;

  const handleFile = async (file: File | undefined) => {
    if (!file || !file.type.startsWith('image/')) {
      setNote('Choose an image file');
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      if (onUploadFile) await onUploadFile(file);
      const local = URL.createObjectURL(file);
      setLocalImageUrl(local);
      setPhoto(local);
      try {
        const http = await ensureHttpImageUrl(local, projectId, file.name);
        // Keep local blob for encode; http URL for cloud AI only
        setWorkingUrl(http);
      } catch {
        /* keep local */
      }
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOpen(false);
  };

  const startCamera = async () => {
    setNote(null);
    try {
      const probe = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: false,
      });
      probe.getTracks().forEach((t) => t.stop());
      setCameraOpen(true);
    } catch {
      setNote('Camera unavailable — use Take photo or Upload instead.');
      cameraRef.current?.click();
    }
  };

  const captureFromCamera = async () => {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth || 1080;
    const h = video.videoHeight || 1440;
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    stopCamera();
    const blob = await new Promise<Blob | null>((resolve) =>
      c.toBlob(resolve, 'image/jpeg', 0.92)
    );
    if (!blob) {
      setNote('Capture failed');
      return;
    }
    await handleFile(new File([blob], `capture_${Date.now()}.jpg`, { type: 'image/jpeg' }));
  };

  /* FUTURE PHASE: helpers for cloud AI look tools
  const withHttp = async (): Promise<string> => {
    if (!workingUrl) throw new Error('Add a photo first');
    return ensureHttpImageUrl(workingUrl, projectId);
  };

  const adoptWorkingImage = (url: string) => {
    setWorkingUrl(url);
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      setLocalImageUrl(url);
      return;
    }
    void (async () => {
      try {
        const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
        if (!res.ok) return;
        const blob = await res.blob();
        const local = URL.createObjectURL(blob);
        setLocalImageUrl((prev) => {
          if (prev?.startsWith('blob:') && prev !== local) URL.revokeObjectURL(prev);
          return local;
        });
      } catch {
        // keep previous localImageUrl
      }
    })();
  };
  */

  /* FUTURE PHASE: cloud AI look tools (credits)
  const runEnhanceCloud = async () => {
    setBusy(true);
    setNote(null);
    try {
      const imageUrl = await withHttp();
      if (!beforeUrl) setBeforeUrl(workingUrl);
      const res = await callEnhancePortrait({ imageUrl, strength: 0.6 });
      adoptWorkingImage(res.processedImageUrl);
      applyCredits(res.credits);
      setNote(`Enhanced (${res.mode})`);
      await refreshCredits();
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Enhance failed');
    } finally {
      setBusy(false);
    }
  };

  const runSkin = async () => {
    if (skinAmount === 0) {
      setNote('Move the skin tone slider first');
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      const imageUrl = await withHttp();
      if (!beforeUrl) setBeforeUrl(workingUrl);
      const res = await callAdjustSkinTone({ imageUrl, amount: skinAmount });
      adoptWorkingImage(res.processedImageUrl);
      applyCredits(res.credits);
      setNote('Skin tone adjusted');
      await refreshCredits();
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Skin tone failed');
    } finally {
      setBusy(false);
    }
  };

  const runSuit = async () => {
    setBusy(true);
    setNote(null);
    try {
      const imageUrl = await withHttp();
      if (!beforeUrl) setBeforeUrl(workingUrl);
      const res = await callSwapOutfit({ imageUrl, presetId: suitId });
      adoptWorkingImage(res.processedImageUrl);
      applyCredits(res.credits);
      setNote(`Suit applied (${res.mode})`);
      await refreshCredits();
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Suit swap failed');
    } finally {
      setBusy(false);
    }
  };

  const runBackground = async () => {
    setBusy(true);
    setNote(null);
    try {
      const imageUrl = await withHttp();
      if (!beforeUrl) setBeforeUrl(workingUrl);
      const bg = SOCIAL_BG_PRESETS.find((b) => b.id === bgId) || SOCIAL_BG_PRESETS[0];
      requestResizeArtboard(platform.width, platform.height);
      const res = await callReplaceBackground({
        imageUrl,
        background: {
          type: bg.type,
          color: bg.color,
          color2: bg.color2,
        },
        width: platform.width,
        height: platform.height,
      });
      adoptWorkingImage(res.processedImageUrl);
      applyCredits(res.credits);
      setNote(`Background set (${res.mode})`);
      await refreshCredits();
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Background failed');
    } finally {
      setBusy(false);
    }
  };
  */

  const placeOnCanvas = async () => {
    if (!workingUrl) return;
    setBusy(true);
    setNote(null);
    try {
      requestResizeArtboard(platform.width, platform.height);
      await onPlaceImage(workingUrl);
      setNote('Still placed on artboard');
      setStep('export');
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Place failed');
    } finally {
      setBusy(false);
    }
  };

  const finishLocalReel = async (
    imageUrl: string,
    startedAt: number,
    durationSec: number
  ) => {
    const style = getMotionStyle(motionStyleId === 'ai-breathe' ? 'push-in' : motionStyleId);
    setReelProgress(
      reelProgressAt('encode', {
        mode: 'local',
        percent: 0,
        startedAt,
        label: 'Encoding preview…',
      })
    );
    const source = imageUrl.startsWith('http')
      ? encodeSourceUrl() || imageUrl
      : imageUrl;
    const local = await createKenBurnsReel({
      imageUrl: source!,
      durationSec,
      width: platform.width,
      height: platform.height,
      startScale: style.startScale,
      endScale: style.endScale,
      panX: style.panX,
      panY: style.panY,
      onProgress: (p) => {
        if (dismissedReelJob.current) return;
        setReelProgress(
          reelProgressAt('encode', {
            mode: 'local',
            percent: p.percent,
            startedAt,
            label: `Encoding ${p.frame} / ${p.total}…`,
          })
        );
      },
    });
    if (dismissedReelJob.current) {
      URL.revokeObjectURL(local.url);
      return;
    }

    let playUrl = local.url;
    let savedNote = `Reel ready (${style.label}). Download when you like it.`;
    if (signedIn && projectId) {
      try {
        setReelProgress(
          reelProgressAt('encode', {
            mode: 'local',
            percent: 100,
            startedAt,
            label: 'Saving reel…',
          })
        );
        const saved = await persistLocalReel({
          projectId,
          blob: local.blob,
          platformId,
          motionStyleId: style.id,
          width: platform.width,
          height: platform.height,
          durationSec,
        });
        URL.revokeObjectURL(local.url);
        playUrl = saved.videoUrl;
        savedNote = `Reel ready (${style.label}). Saved to this project — survives refresh.`;
        void refreshSavedReels();
      } catch (err) {
        console.warn('Could not persist local reel', err);
        savedNote = `Reel ready (${style.label}). Could not save to cloud — download now.`;
      }
    }

    if (reelUrl?.startsWith('blob:')) URL.revokeObjectURL(reelUrl);
    setReelUrl(playUrl);
    setReelProgress(
      reelProgressAt('ready', {
        mode: 'local',
        startedAt,
        label: `Ready · ${style.label}`,
      })
    );
    setStep('export');
    setNote(savedNote);
  };

  const makeLocalReel = async () => {
    const source = encodeSourceUrl();
    if (!source) return;
    dismissedReelJob.current = false;
    clearReelProgressTimer();
    const startedAt = Date.now();
    setBusy(true);
    setNote(null);
    setStep('motion');
    setReelProgress(reelProgressAt('prepare', { mode: 'local', startedAt }));
    try {
      await finishLocalReel(source, startedAt, platform.defaultDurationSec);
    } catch (err) {
      setReelProgress(
        reelProgressAt('failed', {
          mode: 'local',
          startedAt,
          error: err instanceof Error ? err.message : 'Reel failed',
        })
      );
      setNote(err instanceof Error ? err.message : 'Reel failed');
    } finally {
      setBusy(false);
      clearReelProgressTimer();
    }
  };

  /* FUTURE PHASE: AI motion reel (credits)
  const makeAiReel = async () => {
    if (!workingUrl) return;
    if (!signedIn) {
      setNote('Sign in to use AI motion');
      return;
    }
    dismissedReelJob.current = false;
    clearReelProgressTimer();
    const startedAt = Date.now();
    setBusy(true);
    setNote(null);
    setStep('motion');
    setReelProgress(reelProgressAt('prepare', { mode: 'ai', startedAt }));

    reelProgressTimer.current = window.setInterval(() => {
      if (dismissedReelJob.current) return;
      const stage = estimatedAiStage(Date.now() - startedAt);
      setReelProgress((prev) => {
        if (prev.stage === 'encode' || prev.stage === 'ready' || prev.stage === 'failed') {
          return prev;
        }
        return reelProgressAt(stage, { mode: 'ai', startedAt, percent: null });
      });
    }, 800);

    try {
      const imageUrl = await withHttp();
      const res = await callCreateReel({
        imageUrl,
        projectId,
        platformId,
        durationSec: platform.defaultDurationSec,
        width: platform.width,
        height: platform.height,
      });
      if (dismissedReelJob.current) return;

      applyCredits(res.credits);
      await refreshCredits();
      clearReelProgressTimer();

      if (res.mode === 'vendor' && res.processedVideoUrl) {
        let playUrl = res.processedVideoUrl;
        if (res.storagePath?.startsWith('projects/')) {
          try {
            playUrl = await resolveReelPlaybackUrl(res.storagePath);
          } catch {
            // keep signed url
          }
        }
        if (reelUrl?.startsWith('blob:')) URL.revokeObjectURL(reelUrl);
        setReelUrl(playUrl);
        setReelProgress(
          reelProgressAt('ready', {
            mode: 'ai',
            startedAt,
            label: 'Ready · AI motion',
          })
        );
        setStep('export');
        setNote('Reel ready (AI motion). Saved to this project — survives refresh.');
        void refreshSavedReels();
      } else {
        setNote(res.vendorError || 'AI unavailable — encoding free preview instead');
        try {
          await finishLocalReel(
            encodeSourceUrl() || workingUrl,
            startedAt,
            res.durationSec
          );
        } catch (localErr) {
          const msg =
            localErr instanceof Error
              ? localErr.message
              : 'Could not encode free preview';
          setReelProgress(
            reelProgressAt('failed', {
              mode: 'ai',
              startedAt,
              error: `${res.vendorError || 'AI motion failed'}. ${msg}`,
              label: 'Reel failed',
            })
          );
          setNote('AI motion failed. Try free Preview motion (Push-in / Drift).');
        }
      }
    } catch (err) {
      clearReelProgressTimer();
      setReelProgress(
        reelProgressAt('failed', {
          mode: 'ai',
          startedAt,
          error: err instanceof Error ? err.message : 'Reel failed',
        })
      );
      setNote(err instanceof Error ? err.message : 'Reel failed');
    } finally {
      setBusy(false);
      clearReelProgressTimer();
    }
  };
  */

  const dismissReelProgress = () => {
    dismissedReelJob.current = true;
    clearReelProgressTimer();
    setReelProgress(IDLE_REEL_PROGRESS);
    setBusy(false);
    setNote('Progress dismissed.');
  };

  const downloadReel = async () => {
    if (!reelUrl) return;
    try {
      if (reelUrl.startsWith('blob:')) {
        const res = await fetch(reelUrl);
        const blob = await res.blob();
        downloadBlob(
          blob,
          blob.type.includes('mp4') ? 'photo-studio-reel.mp4' : 'photo-studio-reel.webm'
        );
      } else {
        const a = document.createElement('a');
        a.href = reelUrl;
        a.download = 'photo-studio-reel.mp4';
        a.target = '_blank';
        a.rel = 'noopener';
        a.click();
      }
    } catch {
      setNote('Download failed — try opening the video and saving manually');
    }
  };

  const previewSrc = showBefore && beforeUrl ? beforeUrl : workingUrl;
  const canLeaveDestination = true;
  const stepDisabled = (id: StudioStep) => {
    if (id === 'destination') return false;
    if (id === 'photo') return false;
    if (!workingUrl) return true;
    return false;
  };

  const stageChipActive = (id: string) => {
    const order = ['prepare', 'queue', 'generate', 'encode'];
    const cur = order.indexOf(reelProgress.stage);
    const idx = order.indexOf(id);
    if (cur < 0) return false;
    return idx >= 0 && idx <= cur;
  };

  return (
    <div className="ps-photo-studio">
      <header className="ps-photo-studio-header">
        <h3>Photo Studio · Reels</h3>
        <p className="ps-props-hint">
          Pick a platform → photo → optional look → motion preview → export
        </p>
        {/* FUTURE PHASE: AI credits meter
        <div className="ps-photo-studio-credits">
          {signedIn && credits ? (
            <>
              <span>
                AI credits:{' '}
                <strong>
                  {credits.isPro || credits.credits.remaining >= 1_000_000_000
                    ? '∞'
                    : credits.credits.remaining}
                  {!credits.isPro && Number.isFinite(credits.credits.limit)
                    ? ` / ${credits.credits.limit}`
                    : ''}
                </strong>
              </span>
              {credits.credits.reelRemaining !== undefined && (
                <span>
                  Reels:{' '}
                  <strong>
                    {credits.isPro || (credits.credits.reelRemaining ?? 0) >= 1_000_000_000
                      ? '∞'
                      : credits.credits.reelRemaining}
                  </strong>
                </span>
              )}
            </>
          ) : (
            <span className="ps-props-hint">
              {!signedIn
                ? 'Sign in for AI motion (free previews still work).'
                : creditsLoading
                  ? 'Loading credits…'
                  : creditsError
                    ? creditsError
                    : 'Credits unavailable'}
              {signedIn && !creditsLoading && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="ps-photo-studio-upgrade"
                    onClick={() => void refreshCredits()}
                  >
                    Retry
                  </button>
                </>
              )}
            </span>
          )}
        </div>
        */}
      </header>

      <nav className="ps-photo-studio-steps" aria-label="Reel steps">
        {STEPS.map((s) => (
          <button
            key={s.id}
            type="button"
            className={step === s.id ? 'active' : ''}
            disabled={stepDisabled(s.id) || (reelActive && s.id !== 'motion')}
            onClick={() => setStep(s.id)}
          >
            {s.label}
          </button>
        ))}
      </nav>

      <div
        className={`ps-reel-phone ${platform.height / platform.width < 1.5 ? 'ps-reel-phone--short' : ''}`}
      >
        <div className="ps-reel-phone-notch" aria-hidden />
        <div className="ps-reel-phone-screen">
          {reelUrl && !showBefore ? (
            <video className="ps-reel-phone-media" src={reelUrl} controls playsInline loop muted autoPlay />
          ) : previewSrc ? (
            <img className="ps-reel-phone-media" src={previewSrc} alt="Reel preview" />
          ) : (
            <div className="ps-reel-phone-empty">
              <span>9:16 preview</span>
              <span className="ps-props-hint">{platform.shortLabel}</span>
            </div>
          )}
          {showSafeZone && (previewSrc || reelUrl) && (
            <div
              className="ps-reel-safe"
              style={{
                ['--safe-top' as string]: `${platform.safeTop * 100}%`,
                ['--safe-bottom' as string]: `${platform.safeBottom * 100}%`,
              }}
            />
          )}
          {beforeUrl && beforeUrl !== workingUrl && (
            <label className="ps-photo-studio-before">
              <input
                type="checkbox"
                checked={showBefore}
                onChange={(e) => setShowBefore(e.target.checked)}
              />
              Before
            </label>
          )}
        </div>
        <div className="ps-reel-phone-home" aria-hidden />
      </div>

      <label className="ps-reel-safe-toggle">
        <input
          type="checkbox"
          checked={showSafeZone}
          onChange={(e) => setShowSafeZone(e.target.checked)}
        />
        Show {platform.shortLabel} safe zone
      </label>

      {(reelActive || reelProgress.stage === 'failed') && (
        <div className={`ps-reel-progress ${reelProgress.stage === 'failed' ? 'is-failed' : ''}`}>
          <div className="ps-reel-progress-top">
            <strong>{reelProgress.label}</strong>
            {reelProgress.etaHint && (
              <span className="ps-props-hint">{reelProgress.etaHint}</span>
            )}
          </div>
          <div className="ps-reel-progress-bar" role="progressbar" aria-valuenow={reelProgress.percent ?? undefined}>
            <div
              className={`ps-reel-progress-fill ${reelProgress.percent == null ? 'is-indeterminate' : ''}`}
              style={
                reelProgress.percent != null
                  ? { width: `${reelProgress.percent}%` }
                  : undefined
              }
            />
          </div>
          <div className="ps-reel-progress-chips">
            {REEL_STAGE_CHIPS.filter((c) =>
              reelProgress.mode === 'local'
                ? c.id === 'prepare' || c.id === 'encode'
                : true
            ).map((c) => (
              <span
                key={c.id}
                className={stageChipActive(c.id) ? 'active' : ''}
              >
                {c.label}
              </span>
            ))}
          </div>
          {reelProgress.error && (
            <p className="ps-props-hint ps-photo-studio-note">{reelProgress.error}</p>
          )}
          <div className="ps-props-actions">
            {reelActive && (
              <button type="button" onClick={dismissReelProgress}>
                Dismiss
              </button>
            )}
            {reelProgress.stage === 'failed' && (
              <button
                type="button"
                onClick={() => void makeLocalReel()}
              >
                Retry
              </button>
            )}
          </div>
        </div>
      )}

      {step === 'destination' && (
        <div className="ps-photo-studio-section">
          <p className="ps-props-hint">Where will you post? This sets size and safe zones.</p>
          <div className="ps-reel-platforms">
            {REEL_PLATFORMS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={platformId === p.id ? 'active' : ''}
                onClick={() => selectPlatform(p.id)}
              >
                <strong>{p.shortLabel}</strong>
                <span>
                  {p.width}×{p.height}
                </span>
              </button>
            ))}
          </div>
          <p className="ps-props-hint">{platform.tip}</p>
          <div className="ps-props-actions">
            <button
              type="button"
              disabled={!canLeaveDestination}
              onClick={() => {
                selectPlatform(platformId);
                setStep('photo');
              }}
            >
              Continue · {platform.shortLabel}
            </button>
          </div>
        </div>
      )}

      {step === 'photo' && (
        <div className="ps-photo-studio-section">
          <p className="ps-props-hint">Upload or capture a clear portrait. Face mid-frame works best.</p>
          <div className="ps-props-actions">
            <button type="button" disabled={busy || reelActive} onClick={() => fileRef.current?.click()}>
              Upload image
            </button>
            <button type="button" disabled={busy || reelActive} onClick={() => cameraRef.current?.click()}>
              Take photo
            </button>
            <button type="button" disabled={busy || reelActive} onClick={() => void startCamera()}>
              Live camera
            </button>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => void handleFile(e.target.files?.[0])}
          />
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => void handleFile(e.target.files?.[0])}
          />
          {cameraOpen && (
            <div className="ps-photo-studio-camera">
              <video ref={videoRef} playsInline muted autoPlay />
              <div className="ps-props-actions">
                <button
                  type="button"
                  onClick={() =>
                    setFacingMode((f) => (f === 'user' ? 'environment' : 'user'))
                  }
                >
                  Flip
                </button>
                <button type="button" onClick={() => void captureFromCamera()}>
                  Capture
                </button>
                <button type="button" onClick={stopCamera}>
                  Cancel
                </button>
              </div>
            </div>
          )}
          {workingUrl && (
            <div className="ps-props-actions">
              <button type="button" disabled={busy} onClick={() => setStep('look')}>
                Next: Look
              </button>
              <button type="button" disabled={busy} onClick={() => setStep('motion')}>
                Skip look — make reel
              </button>
            </div>
          )}
        </div>
      )}

      {step === 'look' && workingUrl && (
        <div className="ps-photo-studio-section">
          <p className="ps-props-hint">Optional polish — skip anytime and go to motion.</p>
          <div className="ps-reel-look-tabs">
            {(
              PHOTO_STUDIO_AI_ENABLED
                ? ([
                    ['beauty', 'Beauty'],
                    ['skin', 'Skin'],
                    ['suit', 'Suit'],
                    ['background', 'Background'],
                  ] as const)
                : ([['beauty', 'Beauty']] as const)
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={lookTab === id ? 'active' : ''}
                onClick={() => setLookTab(id)}
              >
                {label}
              </button>
            ))}
          </div>

          {lookTab === 'beauty' && (
            <>
              <div className="ps-props-actions">
                {(Object.keys(BEAUTY_PRESETS) as BeautyPresetId[]).map((id) => (
                  <button
                    key={id}
                    type="button"
                    disabled={busy || reelActive}
                    onClick={() => {
                      setBusy(true);
                      setNote(null);
                      void applyClientBeautyToUrl(workingUrl, id)
                        .then((next) => {
                          if (!beforeUrl) setBeforeUrl(workingUrl);
                          setWorkingUrl(next);
                          if (next.startsWith('blob:') || next.startsWith('data:')) {
                            setLocalImageUrl(next);
                          }
                          setNote(`${BEAUTY_PRESETS[id].label} look applied`);
                        })
                        .catch((err) =>
                          setNote(err instanceof Error ? err.message : 'Preset failed')
                        )
                        .finally(() => setBusy(false));
                    }}
                  >
                    {BEAUTY_PRESETS[id].label}
                  </button>
                ))}
              </div>
              {/* FUTURE PHASE: Cloud enhance · 1 credit
              <div className="ps-props-actions">
                <button
                  type="button"
                  disabled={busy || !signedIn || reelActive}
                  onClick={() => void runEnhanceCloud()}
                >
                  {busy ? 'Enhancing…' : 'Cloud enhance · 1 credit'}
                </button>
              </div>
              */}
            </>
          )}

          {/* FUTURE PHASE: Skin / Suit / Background (credits)
          {lookTab === 'skin' && (
            <>
              <label className="ps-photo-studio-slider">
                <span>
                  Skin tone ({skinAmount > 0 ? '+' : ''}
                  {skinAmount})
                </span>
                <input
                  type="range"
                  min={-50}
                  max={50}
                  value={skinAmount}
                  onChange={(e) => setSkinAmount(Number(e.target.value))}
                />
              </label>
              <div className="ps-props-actions">
                <button
                  type="button"
                  disabled={busy || !signedIn || reelActive}
                  onClick={() => void runSkin()}
                >
                  Apply skin · 1 credit
                </button>
              </div>
            </>
          )}

          {lookTab === 'suit' && (
            <>
              <div className="ps-photo-studio-grid">
                {SUIT_CATALOG.map((suit) => (
                  <button
                    key={suit.id}
                    type="button"
                    className={suitId === suit.id ? 'active' : ''}
                    style={{ borderColor: suit.tint }}
                    onClick={() => setSuitId(suit.id)}
                  >
                    <span
                      className="ps-photo-studio-swatch"
                      style={{ background: suit.tint }}
                    />
                    {suit.label}
                    {suit.proOnly ? ' · Pro' : ''}
                  </button>
                ))}
              </div>
              <div className="ps-props-actions">
                <button
                  type="button"
                  disabled={busy || !signedIn || reelActive}
                  onClick={() => void runSuit()}
                >
                  Apply suit · 3 credits
                </button>
              </div>
            </>
          )}

          {lookTab === 'background' && (
            <>
              <div className="ps-photo-studio-grid">
                {SOCIAL_BG_PRESETS.map((bg) => (
                  <button
                    key={bg.id}
                    type="button"
                    className={bgId === bg.id ? 'active' : ''}
                    onClick={() => setBgId(bg.id)}
                  >
                    <span
                      className="ps-photo-studio-swatch"
                      style={{
                        background:
                          bg.type === 'gradient'
                            ? `linear-gradient(180deg, ${bg.color}, ${bg.color2 || bg.color})`
                            : bg.color,
                      }}
                    />
                    {bg.label}
                  </button>
                ))}
              </div>
              <div className="ps-props-actions">
                <button
                  type="button"
                  disabled={busy || !signedIn || reelActive}
                  onClick={() => void runBackground()}
                >
                  Apply background · 2 credits
                </button>
              </div>
            </>
          )}
          */}

          <div className="ps-props-actions">
            <button type="button" disabled={busy || reelActive} onClick={() => setStep('motion')}>
              Looks good — Motion
            </button>
          </div>
        </div>
      )}

      {step === 'motion' && workingUrl && (
        <div className="ps-photo-studio-section">
          <p className="ps-props-hint">
            Preview motion is free.
            {/* FUTURE PHASE: AI motion uses 5 credits + 1 reel quota. */}
          </p>
          <div className="ps-reel-motion-styles">
            {availableMotionStyles.map((m) => (
              <button
                key={m.id}
                type="button"
                className={motionStyleId === m.id ? 'active' : ''}
                disabled={busy || reelActive}
                onClick={() => setMotionStyleId(m.id)}
              >
                <strong>{m.label}</strong>
                <span>{m.costCredits === 0 ? 'Free' : `${m.costCredits} credits`}</span>
                <span className="ps-props-hint">{m.description}</span>
              </button>
            ))}
          </div>
          <div className="ps-props-actions">
            <button
              type="button"
              disabled={busy || reelActive}
              onClick={() => void makeLocalReel()}
            >
              {busy && reelProgress.mode === 'local'
                ? 'Previewing…'
                : `Preview motion · Free`}
            </button>
            {/* FUTURE PHASE: AI motion buttons
            {motionStyle.costCredits > 0 && (
              <button
                type="button"
                disabled={busy || reelActive || !signedIn}
                onClick={() => void makeAiReel()}
              >
                {busy && reelProgress.mode === 'ai'
                  ? 'Generating…'
                  : `AI motion · ${motionStyle.costCredits} credits`}
              </button>
            )}
            {reelUrl && motionStyleId !== 'ai-breathe' && (
              <button
                type="button"
                disabled={busy || reelActive || !signedIn}
                onClick={() => {
                  setMotionStyleId('ai-breathe');
                  void makeAiReel();
                }}
              >
                Upgrade to AI motion · 5 credits
              </button>
            )}
            */}
            {reelUrl && (
              <button type="button" disabled={busy} onClick={() => setStep('export')}>
                Next: Export
              </button>
            )}
          </div>
        </div>
      )}

      {step === 'export' && workingUrl && (
        <div className="ps-photo-studio-section">
          <p className="ps-props-hint">
            {platform.label} · {platform.width}×{platform.height} · {platform.tip}
          </p>
          <div className="ps-props-actions">
            {reelUrl && (
              <button type="button" disabled={busy} onClick={() => void downloadReel()}>
                Download reel
              </button>
            )}
            <button
              type="button"
              disabled={busy || reelActive}
              onClick={() => void placeOnCanvas()}
            >
              Place still on canvas
            </button>
            {!reelUrl && (
              <button type="button" disabled={busy} onClick={() => setStep('motion')}>
                Make motion first
              </button>
            )}
          </div>
        </div>
      )}

      {signedIn && (
        <div className="ps-photo-studio-section ps-reel-library">
          <div className="ps-reel-library-head">
            <strong>Saved reels</strong>
            <button
              type="button"
              className="ps-photo-studio-upgrade"
              disabled={reelsLoading}
              onClick={() => void refreshSavedReels()}
            >
              {reelsLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
          {savedReels.length === 0 ? (
            <p className="ps-props-hint">
              Reels you generate while signed in are saved here and survive refresh.
            </p>
          ) : (
            <div className="ps-reel-library-list">
              {savedReels.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className={reelUrl === r.videoUrl ? 'active' : ''}
                  disabled={busy || reelActive}
                  onClick={() => {
                    if (reelUrl?.startsWith('blob:')) URL.revokeObjectURL(reelUrl);
                    setReelUrl(r.videoUrl);
                    setStep('export');
                    setNote(
                      `Opened saved ${r.mode === 'ai' ? 'AI' : 'preview'} reel`
                    );
                  }}
                >
                  <span>{r.mode === 'ai' ? 'AI' : 'Preview'}</span>
                  <span>
                    {r.width}×{r.height} · {r.durationSec}s
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {note && <p className="ps-props-hint ps-photo-studio-note">{note}</p>}
    </div>
  );
}
